import React, { useState, useEffect } from 'react';
import { 
  IconButton, 
  Avatar, 
  Menu, 
  MenuItem, 
  Switch, 
  ListItemText, 
  Box, 
  Typography, 
  Divider, 
  Snackbar, 
  Alert,
  ListItemIcon,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import axiosInstance from '../api/axiosConfig';
import { Account } from '../App';
import LoginDialog from './LoginDialog';

interface UserMenuProps {
  currentAccount: Account;
  allAccounts: Account[];
  onLogout: () => void;
  onLogoutAll: () => void;
  onSwitchAccount: (userId: string) => void;
  onAddAccount: (token: string, userId: string, username: string) => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ 
  currentAccount, 
  allAccounts, 
  onLogout, 
  onLogoutAll,
  onSwitchAccount,
  onAddAccount 
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [downloadAudio, setDownloadAudio] = useState<boolean>(true);
  const [message, setMessage] = useState('');
  const [openAccountSwitcher, setOpenAccountSwitcher] = useState(false);
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [openLogoutAllDialog, setOpenLogoutAllDialog] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [videoConfig, setVideoConfig] = useState({
    preferedCodec: 'h264',
    availableCodecs: []
  });

  useEffect(() => {
    if (!currentAccount?.userId) return;
    (async () => {
      try {
        const resp = await axiosInstance.get(`${backendUrl}/api/user/${currentAccount.userId}`);
        if (resp.data && typeof resp.data.downloadAudio === 'boolean') {
          setDownloadAudio(resp.data.downloadAudio);
        }
        const vresp = await axiosInstance.get(`${backendUrl}/api/config/video`);
        setVideoConfig({
          preferedCodec: vresp.data.preferedCodec,
          availableCodecs: vresp.data.availableCodecs
        });
      } catch (e) {
        console.warn('Failed to load user settings', e);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount?.userId]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleToggleCodec = async (newCodec: string) => {
    try {
      const resp = await axiosInstance.patch(`${backendUrl}/api/config/video/preferedCodec`, { 
        preferedCodec: newCodec 
      });
      
      setVideoConfig(prev => ({
        ...prev,
        preferedCodec: resp.data.preferedCodec
      }));
      
      setMessage('Codec updated successfully');
    } catch (e) {
      console.error('Failed to update codec', e);
      setMessage('Failed to update codec');
    }
  };

  const handleToggleAudio = async () => {
    const newVal = !downloadAudio;
    setDownloadAudio(newVal);
    try {
      const resp = await axiosInstance.patch(`${backendUrl}/api/user/settings`, { downloadAudio: newVal });
      setDownloadAudio(resp.data.downloadAudio);
      setMessage('Settings updated');
    } catch (e) {
      console.error('Failed to update settings', e);
      setMessage('Failed to update settings');
      setDownloadAudio(!newVal);
    }
  };

  const handleOpenAccountSwitcher = () => {
    setOpenAccountSwitcher(true);
    handleClose();
  };

  const handleCloseAccountSwitcher = () => {
    setOpenAccountSwitcher(false);
  };

  const handleSwitchAccount = (userId: string) => {
    onSwitchAccount(userId);
    handleCloseAccountSwitcher();
  };

  const handleAddAccount = () => {
    setOpenLoginDialog(true);
    handleClose();
  };

  const handleLogoutAllConfirm = () => {
    onLogoutAll();
    setOpenLogoutAllDialog(false);
  };

  const otherAccounts = allAccounts.filter(acc => acc.userId !== currentAccount.userId);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Account indicator for multiple accounts */}
        {allAccounts.length > 1 && (
          <Chip 
            size="small" 
            label={allAccounts.length} 
            color="primary" 
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
        
        <IconButton onClick={handleOpen} size="small" sx={{ p: 0 }}>
          <Avatar sx={{ bgcolor: 'primary.dark', width: 32, height: 32, fontSize: '0.8rem' }}>
            {currentAccount.username.charAt(0).toUpperCase()}
          </Avatar>
        </IconButton>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {currentAccount.username}
          </Typography>
          {allAccounts.length > 1 && (
            <Typography variant="caption" color="text.secondary">
              {allAccounts.length} account{allAccounts.length > 1 ? 's' : ''} logged in
            </Typography>
          )}
        </Box>
        
        <Divider />
        
        {/* Account Management Section */}
        {otherAccounts.length > 0 && (
          <>
            <MenuItem onClick={handleOpenAccountSwitcher}>
              <ListItemIcon>
                <SwapHorizIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Switch Account" />
              <Typography variant="caption" color="text.secondary">
                {otherAccounts.length}
              </Typography>
            </MenuItem>
          </>
        )}
        
        <MenuItem onClick={handleAddAccount}>
          <ListItemIcon>
            <AddCircleOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="add account" />
        </MenuItem>
        
        <Divider />
        
        {/* Settings Section */}
        <MenuItem>
        <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="audio" />
          <Switch checked={downloadAudio} onChange={handleToggleAudio} size="small" />
        </MenuItem>
        <MenuItem>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption">H.264</Typography>
            <Switch 
              checked={videoConfig.preferedCodec === 'vp9'} 
              onChange={(e) => handleToggleCodec(e.target.checked ? 'vp9' : 'h264')}
              size="small" 
            />
            <Typography variant="caption">VP9</Typography>
          </Box>
        </MenuItem>
        <Divider />
        
        {/* Logout Section */}
        <MenuItem onClick={() => { handleClose(); onLogout(); }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" secondary={currentAccount.username} />
        </MenuItem>
        
        {allAccounts.length > 1 && (
          <MenuItem onClick={() => { handleClose(); setOpenLogoutAllDialog(true); }}>
            <ListItemIcon>
              <ExitToAppIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Logout All" 
              secondary={`${allAccounts.length} accounts`}
            />
          </MenuItem>
        )}
      </Menu>

      {/* Account Switcher Dialog */}
      <Dialog open={openAccountSwitcher} onClose={handleCloseAccountSwitcher} maxWidth="xs" fullWidth>
        <DialogTitle>Switch Account</DialogTitle>
        <DialogContent>
          <List>
            {allAccounts.map(account => (
              <ListItemButton
                key={account.userId}
                onClick={() => handleSwitchAccount(account.userId)}
                selected={account.userId === currentAccount.userId}
                sx={{ borderRadius: 1, mb: 1, '&:hover': { backgroundColor: 'action.hover' } }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: account.userId === currentAccount.userId ? 'primary.main' : 'grey.600' }}>
                    {account.username.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText 
                  primary={account.username}
                  secondary={account.userId === currentAccount.userId ? 'Current account' : 'Switch to this account'}
                />
                {account.userId === currentAccount.userId && (
                  <CheckCircleIcon color="primary" />
                )}
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAccountSwitcher}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Logout All Confirmation Dialog */}
      <Dialog open={openLogoutAllDialog} onClose={() => setOpenLogoutAllDialog(false)}>
        <DialogTitle>Logout All Accounts</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to logout from all {allAccounts.length} accounts? You will need to login again to access your content.
          </DialogContentText>
          <List sx={{ mt: 2 }}>
            {allAccounts.map(account => (
              <ListItem key={account.userId} dense>
                <ListItemAvatar>
                  <Avatar sx={{ width: 30, height: 30, fontSize: '0.9rem' }}>
                    {account.username.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={account.username} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLogoutAllDialog(false)}>Cancel</Button>
          <Button onClick={handleLogoutAllConfirm} color="error" variant="contained">
            Logout All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Account Dialog */}
      <LoginDialog 
        open={openLoginDialog}
        onClose={() => setOpenLoginDialog(false)}
        onLogin={onAddAccount}
        isAddingAccount={true}
        existingAccounts={allAccounts}
      />

      {/* Snackbar for messages */}
      <Snackbar open={!!message} autoHideDuration={3000} onClose={() => setMessage('')}>
        <Alert onClose={() => setMessage('')} severity="info" sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserMenu;